import { useMemo } from "react";
import { findGoverningCase } from "../calc/governingCase";
import { findScenario } from "../data/scenarios";
import { toPsvCase, type CaseState } from "./caseState";
import { gaugeLabel, type PressureUnit } from "./pressureUnit";
import { sizingSheetRows, fluidLabel, fluidLabelShort } from "./sheetRows";
import { sizingFormulas } from "./formulas";
import { rateCalcBasis, rateCalcContext } from "./rateCalc";
import { PipingSheet } from "./PipingSheet";
import { FlareSheet } from "./FlareSheet";
import type { PipingState } from "./pipingState";
import type { FlareState } from "./flareState";
import { downloadCsv, buildReportCsv } from "./csvExport";

const nf = (v: number, d = 0) =>
  v.toLocaleString("ko-KR", {
    minimumFractionDigits: d,
    maximumFractionDigits: d,
  });

/**
 * 제출용 계산서.
 *
 * 검토자가 표준서를 펴놓고 따라올 수 있도록 **중간값마다 근거 식·조항**을
 * 함께 싣는다. 화면에서 그대로 인쇄하면 PDF 가 되고, 인쇄용 CSS 가
 * 버튼·탭 같은 화면 장식을 걷어낸다.
 */
/** 계산서에 무엇을 실을지. 배관·플레어는 안 한 검토일 수 있으므로 고르게 한다. */
export interface SheetInclude {
  readonly sizing: boolean;
  readonly piping: boolean;
  readonly flare: boolean;
}

export function CalcSheet({
  cases,
  unit,
  piping,
  flare,
  include,
  onIncludeChange,
}: {
  cases: readonly CaseState[];
  unit: PressureUnit;
  piping: PipingState;
  flare: FlareState;
  include: SheetInclude;
  onIncludeChange: (next: SheetInclude) => void;
}) {
  const sheets = useMemo(
    () =>
      cases.map((c) => {
        const psvCase = toPsvCase(c, unit);
        return { state: c, psvCase, result: findGoverningCase(psvCase) };
      }),
    [cases, unit],
  );

  const today = new Date().toLocaleDateString("ko-KR", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  return (
    <>
      <section className="card no-print">
        <div className="row-head">
          <h2>계산서 출력</h2>
          <div className="savebar__actions">
            <button
              type="button"
              className="btn"
              onClick={() => window.print()}
            >
              인쇄 / PDF 저장
            </button>
            <button
              type="button"
              className="btn"
              onClick={() =>
                downloadCsv(
                  buildReportCsv({
                    sources: sheets.map((s) => ({
                      psvCase: s.psvCase,
                      result: s.result,
                    })),
                    piping,
                    flare,
                    unit,
                    include,
                  }),
                )
              }
            >
              엑셀용 CSV 내보내기
            </button>
          </div>
        </div>
        <div className="sheetpick">
          <span className="muted">계산서에 실을 항목</span>
          <label>
            <input
              type="checkbox"
              checked={include.sizing}
              onChange={(e) =>
                onIncludeChange({ ...include, sizing: e.target.checked })
              }
            />
            밸브 사이징 ({cases.length}건)
          </label>
          <label>
            <input
              type="checkbox"
              checked={include.piping}
              onChange={(e) =>
                onIncludeChange({ ...include, piping: e.target.checked })
              }
            />
            배관 검토
          </label>
          <label>
            <input
              type="checkbox"
              checked={include.flare}
              onChange={(e) =>
                onIncludeChange({ ...include, flare: e.target.checked })
              }
            />
            플레어 헤더
          </label>
        </div>
        <p className="hint">
          <b>인쇄 / PDF 저장</b>을 누르면 브라우저 인쇄창이 열립니다. 프린터
          목록에서 <b>&quot;PDF로 저장&quot;</b>을 고르시면 제출용 파일이
          만들어집니다. 화면의 버튼·탭은 인쇄물에 나오지 않습니다.
          <br />
          <b>CSV</b>는 엑셀에서 바로 열립니다 — 검토자가 숫자를 직접 확인·수정할
          때 쓰십시오.
        </p>
      </section>

      {include.sizing && cases.length === 0 && (
        <section className="card">
          <p className="hint">
            밸브가 없어 사이징 계산서를 만들 수 없습니다. 케이스 검토에서
            밸브를 추가하시거나, 위에서 다른 항목을 고르십시오.
          </p>
        </section>
      )}

      {include.sizing &&
        sheets.map(({ state, psvCase, result }) => {
        const gov = result.governing;
        const sel = result.selection;
        return (
          <section className="card sheet" key={state.id}>
            <header className="sheet__head">
              <div>
                <h2 className="sheet__title">안전밸브 사이징 계산서</h2>
                <p className="sheet__sub">
                  API 520 Part I 10판(2020) · API 521(2020) · API 526 4판
                </p>
              </div>
              <div className="sheet__meta">
                <div>
                  <dt>태그</dt>
                  <dd className="sheet__tag">{state.tag || "—"}</dd>
                </div>
                <div>
                  <dt>작성일</dt>
                  <dd>{today}</dd>
                </div>
              </div>
            </header>

            <h3 className="sheet__h3">1. 검토 조건</h3>
            <table className="sheet__table">
              <tbody>
                <tr>
                  <th>보호 설비</th>
                  <td>{state.equipment || "—"}</td>
                  <th>설정압</th>
                  {/* 계산은 kPa 로 하지만, 설계자가 입력한 단위도 함께 남긴다.
                      계산서만 보고도 원 입력값을 확인할 수 있어야 한다. */}
                  <td>
                    {unit === "kPa" ? (
                      <>{nf(psvCase.setPressureKpaGauge, 1)} kPag</>
                    ) : (
                      <>
                        {state.setPressure} {gaugeLabel(unit)}
                        <span className="muted">
                          {" "}
                          ({nf(psvCase.setPressureKpaGauge, 1)} kPag)
                        </span>
                      </>
                    )}
                  </td>
                </tr>
                <tr>
                  <th>설치 형태</th>
                  <td>
                    {psvCase.installation === "single"
                      ? "단일 설치"
                      : "복수 설치"}
                  </td>
                  <th>허용 과압</th>
                  <td>
                    운전 {psvCase.installation === "single" ? 10 : 16} % ·
                    화재 21 %
                    <span className="muted"> (5.4.2 / 5.4.3)</span>
                  </td>
                </tr>
              </tbody>
            </table>

            <h3 className="sheet__h3">2. 과압 시나리오 검토</h3>
            <div className="table-scroll">
              <table className="sheet__table sheet__table--grid sheet__table--scenarios">
                <thead>
                  <tr>
                    <th className="col-no">No.</th>
                    <th className="col-name">시나리오</th>
                    <th className="col-desc">상황 설명</th>
                    <th className="col-fluid">유체</th>
                    <th className="col-flow">분출용량</th>
                    <th className="col-op">과압</th>
                    <th className="col-p1">P1</th>
                    <th className="col-area">소요 면적</th>
                    <th className="col-verdict">판정</th>
                  </tr>
                </thead>
                <tbody>
                  {result.ranked.map((e) => {
                    const def = findScenario(e.entry.item);
                    const isGov = gov?.entry.id === e.entry.id;
                    return (
                      <tr key={e.entry.id} className={isGov ? "row--gov" : ""}>
                        <td className="cell">{e.entry.item}</td>
                        <td className="col-name">
                          {def?.conditionKo}
                          <br />
                          <span className="muted">{def?.section}</span>
                        </td>
                        <td className="cell--desc">
                          {e.entry.description || (
                            <span className="muted">—</span>
                          )}
                        </td>
                        <td>
                          {e.detail ? fluidLabelShort(e.detail.kind) : "—"}
                        </td>
                        <td className="cell">
                          {nf(e.flow.value)}
                          <br />
                          <span className="muted">{e.flow.unit}</span>
                        </td>
                        <td className="cell">{e.overpressurePercent} %</td>
                        <td className="cell">
                          {nf(e.relievingPressureKpaAbs, 1)}
                        </td>
                        <td className="cell">
                          {e.requiredAreaMm2 === null
                            ? "—"
                            : `${nf(e.requiredAreaMm2)} mm²`}
                        </td>
                        <td>{isGov ? "지배" : e.error ? "불가" : ""}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {result.excludedCount > 0 && (
              <p className="sheet__note">
                검토에서 제외한 시나리오 {result.excludedCount}건은 위 표에
                포함되지 않았습니다.
              </p>
            )}

            <h3 className="sheet__h3">3. 시나리오별 계산 상세</h3>
            {result.ranked.length === 0 && (
              <p className="sheet__note">검토한 시나리오가 없습니다.</p>
            )}
            {result.ranked.map((e) => {
              const def = findScenario(e.entry.item);
              const isGov = gov?.entry.id === e.entry.id;
              return (
                <div
                  key={e.entry.id}
                  className={"sheet__case" + (isGov ? " sheet__case--gov" : "")}
                >
                  <h4 className="sheet__caseTitle">
                    {e.entry.item}. {e.entry.label}
                    <span className="muted"> (API 521 {def?.section})</span>
                    {isGov && <span className="sheet__govTag">지배 케이스</span>}
                  </h4>

                  <table className="sheet__table">
                    <tbody>
                      {e.entry.description && (
                        <tr>
                          <th>상황 설명</th>
                          <td colSpan={3}>{e.entry.description}</td>
                        </tr>
                      )}
                      <tr>
                        <th>유체 상태</th>
                        <td>{e.detail ? fluidLabel(e.detail.kind) : "—"}</td>
                        <th>소요 분출용량</th>
                        <td>
                          {nf(e.flow.value)} {e.flow.unit}
                        </td>
                      </tr>
                      <tr>
                        <th>상황 구분</th>
                        <td>
                          {e.entry.contingency === "fire" ? "화재" : "운전"} ·
                          허용 과압 {e.overpressurePercent} %
                        </td>
                        <th>배출 압력 P1</th>
                        <td>{nf(e.relievingPressureKpaAbs, 1)} kPa abs</td>
                      </tr>
                      <tr>
                        <th>적용 식</th>
                        <td colSpan={3}>{e.equation ?? "—"}</td>
                      </tr>
                    </tbody>
                  </table>

                  {/* 소요 분출용량을 계산기로 구했다면 그 근거도 함께 싣는다.
                      유량이 어디서 나왔는지가 계산서에서 가장 자주 지적받는
                      부분이다. */}
                  {(() => {
                    const row = state.rows.find((r) => r.id === e.entry.id);
                    if (!row) return null;
                    const basis = rateCalcBasis(
                      row.rateCalc,
                      rateCalcContext(row, state, unit),
                      row.flow,
                    );
                    if (!basis) return null;
                    return (
                      <>
                        <h5 className="sheet__h5">
                          소요 분출용량 산정 근거 — {basis.title}
                        </h5>
                        {basis.mismatch && (
                          <p className="sheet__note sheet__note--warn">
                            {basis.mismatch}
                          </p>
                        )}
                        <table className="sheet__table sheet__table--grid">
                          <thead>
                            <tr>
                              <th>항목</th>
                              <th>값</th>
                              <th>근거</th>
                            </tr>
                          </thead>
                          <tbody>
                            {basis.inputs.map((r) => (
                              <tr key={`in-${r.label}`}>
                                <td>{r.label}</td>
                                <td className="cell">{r.value}</td>
                                <td className="muted">{r.source}</td>
                              </tr>
                            ))}
                            {basis.results.map((r) => (
                              <tr key={`out-${r.label}`}>
                                <td>
                                  <b>{r.label}</b>
                                </td>
                                <td className="cell">
                                  <b>{r.value}</b>
                                </td>
                                <td className="muted">{r.source}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        {basis.warnings.length > 0 && (
                          <ul className="sheet__warnings">
                            {basis.warnings.map((w) => (
                              <li key={w}>{w}</li>
                            ))}
                          </ul>
                        )}
                      </>
                    );
                  })()}

                  {e.error !== null ? (
                    <p className="sheet__note sheet__note--warn">
                      계산 불가 — {e.error}
                    </p>
                  ) : (
                    e.detail && (
                      <>
                        {/* 계산 과정 — 기호식 · 대입식 · 결과 */}
                        <div className="calcsteps">
                          {sizingFormulas(e.detail).map((f, idx) => (
                            <div className="calcstep" key={f.label + idx}>
                              <div className="calcstep__head">
                                <span className="calcstep__label">
                                  {f.label}
                                </span>
                                <span className="calcstep__src">
                                  {f.source}
                                </span>
                              </div>
                              <div className="calcstep__eq">{f.symbolic}</div>
                              {f.substituted && (
                                <div className="calcstep__eq calcstep__eq--sub">
                                  {f.substituted}
                                </div>
                              )}
                              <div className="calcstep__res">
                                = <b>{f.result}</b>
                              </div>
                              {f.note && (
                                <div className="calcstep__note">{f.note}</div>
                              )}
                            </div>
                          ))}
                        </div>

                        {/* 계수 요약 — 위 식에 들어간 값들을 한눈에 */}
                        <h5 className="sheet__h5">적용 계수 요약</h5>
                        <table className="sheet__table sheet__table--grid">
                          <thead>
                            <tr>
                              <th>항목</th>
                              <th>값</th>
                              <th>근거</th>
                            </tr>
                          </thead>
                          <tbody>
                            {sizingSheetRows(e.detail).map((r) => (
                              <tr key={r.label}>
                                <td>{r.label}</td>
                                <td className="cell">{r.value}</td>
                                <td className="muted">{r.source}</td>
                              </tr>
                            ))}
                            <tr>
                              <td>
                                <b>소요 유효 오리피스 면적</b>
                              </td>
                              <td className="cell">
                                <b>{nf(e.requiredAreaMm2!)} mm²</b>
                              </td>
                              <td className="muted">{e.equation}</td>
                            </tr>
                          </tbody>
                        </table>
                      </>
                    )
                  )}
                </div>
              );
            })}

            {gov === null ? (
              <p className="sheet__note sheet__note--warn">
                계산에 성공한 시나리오가 없어 지배 케이스를 정할 수 없습니다.
              </p>
            ) : (
              <>
                <h3 className="sheet__h3">4. 밸브 선정</h3>
                <table className="sheet__table">
                  <tbody>
                    <tr>
                      <th>지배 케이스</th>
                      <td colSpan={3}>
                        <b>
                          {gov.entry.item}. {gov.entry.label}
                        </b>
                        <span className="muted">
                          {" "}
                          — 소요 면적 {nf(gov.requiredAreaMm2!)} mm² 로 최대
                        </span>
                      </td>
                    </tr>
                  </tbody>
                </table>
                {sel?.ok ? (
                  <>
                    <table className="sheet__table">
                      <tbody>
                        <tr>
                          <th>선정 오리피스</th>
                          <td className="sheet__big">
                            {sel.orifice.designation}
                            {sel.valveCount > 1 &&
                              ` × ${sel.valveCount}개 병렬`}
                          </td>
                          <th>유효 면적</th>
                          <td>
                            {nf(sel.orifice.effectiveAreaMm2, 1)} mm² (
                            {sel.orifice.effectiveAreaIn2} in²)
                          </td>
                        </tr>
                        <tr>
                          <th>소요 면적</th>
                          <td>
                            {nf(sel.areaPerValveMm2)} mm²
                            {sel.valveCount > 1 && " (밸브 1개당)"}
                          </td>
                          <th>여유율</th>
                          <td>{sel.marginPct.toFixed(1)} %</td>
                        </tr>
                      </tbody>
                    </table>

                    <h4 className="sheet__h4">
                      제공 밸브 규격 (API 526 Table 2~15)
                    </h4>
                    <table className="sheet__table sheet__table--grid">
                      <thead>
                        <tr>
                          <th>규격</th>
                          <th>입구</th>
                          <th>출구</th>
                          <th>입구 클래스</th>
                          <th>출구 클래스</th>
                        </tr>
                      </thead>
                      <tbody>
                        {sel.orifice.valveSizes.map((v, i) => (
                          <tr key={`${v.size}-${v.inletClass}-${i}`}>
                            <td>{v.size}</td>
                            <td className="cell">{v.inletNps}&quot;</td>
                            <td className="cell">{v.outletNps}&quot;</td>
                            <td className="cell">{v.inletClass}</td>
                            <td className="cell">{v.outletClass}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </>
                ) : (
                  <p className="sheet__note sheet__note--warn">
                    {sel?.message ?? "오리피스를 선정할 수 없습니다."}
                  </p>
                )}
              </>
            )}

            {result.ranked.some((e) => e.warnings.length > 0) && (
              <>
                <h3 className="sheet__h3">5. 확인 필요 사항</h3>
                {/* 경고는 시나리오별로 나오므로 어느 케이스의 것인지 함께 적는다 */}
                <ul className="sheet__warnings">
                  {result.ranked.flatMap((e) =>
                    e.warnings.map((w) => (
                      <li key={`${e.entry.id}-${w}`}>
                        <b>
                          {e.entry.item}. {e.entry.label}
                        </b>{" "}
                        — {w}
                      </li>
                    )),
                  )}
                </ul>
              </>
            )}

            <footer className="sheet__foot">
              <p>
                근거: API Standard 520 Part I, 10th Edition (2020) · API Standard
                521 (2020) · API Standard 526, 4th Edition (1995)
              </p>
              <p className="muted">
                본 계산서는 예비 사이징(effective area · effective coefficient of
                discharge) 기준입니다. 최종 선정은 제작사의 인증 면적·인증
                배출계수로 재확인해야 합니다 (API 520 Part I 5.2.4).
              </p>
            </footer>
            </section>
          );
        })}

      {include.piping && (
        <PipingSheet value={piping} unit={unit} today={today} />
      )}

      {include.flare && <FlareSheet value={flare} unit={unit} today={today} />}

      {!include.sizing && !include.piping && !include.flare && (
        <section className="card">
          <p className="hint">
            실을 항목을 하나 이상 고르십시오.
          </p>
        </section>
      )}
    </>
  );
}
